import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { fadeInUp, staggerContainer } from '../hooks/useScrollAnimation';
import SectionHeading from './common/SectionHeading';
import Button from './common/Button';
import { BOOK_CALL_URL } from '../config/links';

/**
 * The page asks a club to hand its supporter relationship to a third party and
 * says the whole thing is free. That reliably raises the same four questions,
 * and previously the page answered none of them. Answering them here is worth
 * more than another feature grid.
 */
const faqs = [
  {
    question: 'What does this actually cost us?',
    answer:
      'Nothing up front. ESF funds the build, the card programme and the merchant onboarding, and earns from a share of the revenue the platform generates. If the platform earns nothing, you owe nothing.',
  },
  {
    question: 'Who issues the cards, and who holds the licence?',
    answer:
      'Cards are issued by a regulated banking partner under their licence, on the Mastercard network. ESF is the technology provider and programme operator — we are not a bank and do not hold client funds.',
  },
  {
    question: 'What do we have to do, and what does it need from our IT?',
    answer:
      'No integration is required on your side. In practice a club spends one to two hours a week on approvals and marketing input; ESF runs the platform, the merchant network and the support.',
  },
  {
    question: 'Who owns the fan data, and what happens if we leave?',
    answer:
      'The club owns its supporter data. It is processed inside the EU under GDPR and is exportable at any time. Contract length, notice period and wind-down terms are set out in the club agreement.',
  },
];

const Faq = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="relative py-24 md:py-32 bg-esf-dark">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Questions" headline="The things clubs ask first" />

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="mt-14 space-y-3"
        >
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div key={faq.question} variants={fadeInUp}>
                <div className="glass-panel-static overflow-hidden">
                  <h3>
                    <button
                      type="button"
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      aria-expanded={isOpen}
                      aria-controls={`faq-answer-${index}`}
                      className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left"
                    >
                      <span className="text-body-lg font-semibold text-esf-white">
                        {faq.question}
                      </span>
                      <Plus
                        size={20}
                        aria-hidden="true"
                        className={`flex-shrink-0 text-esf-blue transition-transform duration-300 ${
                          isOpen ? 'rotate-45' : ''
                        }`}
                      />
                    </button>
                  </h3>
                  {isOpen && (
                    <div id={`faq-answer-${index}`} className="px-5 md:px-6 pb-5 md:pb-6 -mt-1">
                      <p className="text-body text-esf-gray-300 max-w-2xl">{faq.answer}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="text-body text-esf-gray-300">Something we have not covered?</p>
          <div className="mt-4">
            <Button href={BOOK_CALL_URL} variant="secondary" size="md">
              Ask us directly
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Faq;
